import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { logListenerError } from 'features/firebase/services/listenerErrorLog';
import { logger } from 'features/logger/logger';
import type { DbStudyEditorFullExample } from '@hub/common';

export type StudyEditorFullExamplesSubscriptionData = {
  isSnapshotReceived: boolean;
  examples: DbStudyEditorFullExample[];
};

export const adminStudyEditorFullExamplesApi = createApi({
  reducerPath: 'adminStudyEditorFullExamplesApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['fullExamples'],
  endpoints: builder => ({
    adminStudyEditorFullExamplesSubscription: builder.query<
      StudyEditorFullExamplesSubscriptionData,
      undefined
    >({
      providesTags: ['fullExamples'],
      keepUnusedDataFor: 3600,
      queryFn() {
        return { data: { isSnapshotReceived: false, examples: [] } };
      },

      async onCacheEntryAdded(
        _params,
        // eslint-disable-next-line @typescript-eslint/unbound-method -- it's how it is in Redux docs
        { updateCachedData, cacheEntryRemoved }
      ) {
        logger.info('adminStudyEditorFullExamplesApi onCacheEntryAdded');

        let callbackId: string | undefined;

        try {
          callbackId = await FirebaseFirestore.addCollectionSnapshotListener(
            {
              reference: '/studyEditorFullExamples',
            },
            (event, error) => {
              if (error) {
                logListenerError(
                  'Unable to subscribe to studyEditorFullExamples collection',
                  error as Error
                );
              } else {
                if (!event) return;

                logger.debug(
                  `adminStudyEditorFullExamplesApi onSnapshot (length: ${event.snapshots.length.toString()})`
                );
                updateCachedData(draft => {
                  draft.examples = event.snapshots
                    .map(d => d.data as DbStudyEditorFullExample)
                    .sort((a, b) => a.name.localeCompare(b.name));
                });
              }
            }
          );
        } catch (error) {
          logger.error(
            'adminStudyEditorFullExamplesApi error',
            undefined,
            error as Error
          );
          throw new Error(
            'Error while retrieving full implemenentation examples for study editor'
          );
        }

        await cacheEntryRemoved;
        if (callbackId) {
          void FirebaseFirestore.removeSnapshotListener({
            callbackId,
          });
        }
      },
    }),
  }),
});

export const { useAdminStudyEditorFullExamplesSubscriptionQuery } =
  adminStudyEditorFullExamplesApi;
